<!-- 平台管理端 — 商家入驻审核列表 -->
<template>
  <ContentWrap title="入驻申请列表">
    <el-form :inline="true" :model="queryParams" class="-mb-15px">
      <el-form-item label="公司名称">
        <el-input v-model="queryParams.companyName" placeholder="公司名称" clearable class="!w-200px" @keyup.enter="getList" />
      </el-form-item>
      <el-form-item label="审核状态">
        <el-select v-model="queryParams.auditStatus" clearable placeholder="全部" class="!w-140px">
          <el-option label="待审核" :value="0" />
          <el-option label="审核通过" :value="1" />
          <el-option label="审核拒绝" :value="2" />
          <el-option label="已撤销" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="getList">
          <Icon class="mr-5px" icon="ep:search" />搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />重置
        </el-button>
        <el-button v-hasPermi="['merchant:apply:create']" type="primary" plain @click="openCreate">
          <Icon class="mr-5px" icon="ep:plus" />新增入驻
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap class="mt-16px">
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="公司名称" min-width="180" prop="companyName" show-overflow-tooltip />
      <el-table-column label="店铺名称" min-width="150" prop="shopName" show-overflow-tooltip />
      <el-table-column label="商家类型" width="110" align="center">
        <template #default="{ row }">{{ merchantTypeLabel(row.merchantType) }}</template>
      </el-table-column>
      <el-table-column label="法人/联系人" width="130" prop="legalPerson" show-overflow-tooltip />
      <el-table-column label="联系电话" width="130" prop="contactPhone" />
      <el-table-column label="联系邮箱" min-width="170" prop="contactEmail" show-overflow-tooltip />
      <el-table-column label="套餐" min-width="120" show-overflow-tooltip>
        <template #default="{ row }">
          <span v-if="row.planId">¥{{ ((row.planPrice || 0) / 100).toFixed(2) }} / {{ row.planSpuLimit === -1 || row.planSpuLimit == null ? '不限' : row.planSpuLimit + '件' }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="支付" width="90" align="center">
        <template #default="{ row }">
          <el-tag size="small" :type="payStatusTag(row.payStatus)">{{ payStatusLabel(row.payStatus) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="审核状态" width="100" align="center">
        <template #default="{ row }">
          <el-tag :type="statusTagType(row.auditStatus)">{{ statusLabel(row.auditStatus) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="申请时间" width="170" prop="createTime" :formatter="dateFormatter" />
      <el-table-column label="操作" width="120" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" link @click="openDetail(row.id)">
            {{ row.auditStatus === 0 ? '审核' : '查看' }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 新增入驻申请弹窗：加宽 + 证件图单独一行，避免第三张图挤出弹窗 -->
  <el-dialog v-model="dialogVisible" title="新增入驻申请" width="880px" destroy-on-close class="merchant-apply-dialog">
    <el-form ref="createFormRef" :model="createForm" :rules="createRules" label-width="110px">
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="店铺名称" prop="shopName">
            <el-input v-model="createForm.shopName" placeholder="请输入店铺名称" maxlength="100" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="商家类型" prop="merchantType">
            <el-select v-model="createForm.merchantType" class="!w-full">
              <el-option v-for="item in merchantTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col v-if="planOptions.length" :span="12">
          <el-form-item label="入驻套餐" prop="planId">
            <el-select v-model="createForm.planId" clearable filterable class="!w-full" placeholder="选填；商城入驻建议选择">
              <el-option
                v-for="p in planOptions"
                :key="p.id"
                :label="`${p.name} · ¥${((p.price || 0) / 100).toFixed(2)} · ${p.spuLimit == null || p.spuLimit < 0 ? '不限' : p.spuLimit + '件'}`"
                :value="p.id"
              />
            </el-select>
            <div class="text-12px text-[var(--el-text-color-secondary)] mt-4px">
              付费档代提视为运营已确认收款，直接进入待审核
            </div>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="法人姓名" prop="legalPerson">
            <el-input v-model="createForm.legalPerson" placeholder="请输入法人姓名" maxlength="50" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="联系电话" prop="contactPhone">
            <el-input v-model="createForm.contactPhone" placeholder="请输入联系电话" maxlength="20" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="联系邮箱" prop="contactEmail">
            <el-input v-model="createForm.contactEmail" placeholder="选填，用于接收审核通知" maxlength="100" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="信用代码" prop="creditCode">
            <el-input v-model="createForm.creditCode" placeholder="选填，统一社会信用代码" maxlength="64" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="法人身份证号" prop="idCardNo">
            <el-input v-model="createForm.idCardNo" placeholder="选填" maxlength="32" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="登录账号" prop="loginUsername">
            <el-input v-model="createForm.loginUsername" placeholder="选填，审核通过后商家登录用" maxlength="50" />
          </el-form-item>
        </el-col>
        <el-col :span="16">
          <el-form-item label="店铺地址" prop="address">
            <el-input v-model="createForm.address" placeholder="可手动输入或地图选点" maxlength="255" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label-width="0">
            <el-button type="primary" plain class="!w-full" @click="openAmapPicker">
              <Icon icon="ep:location" class="mr-5px" /> 地图选点
            </el-button>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="经度" prop="longitude">
            <el-input-number v-model="createForm.longitude" :precision="6" class="!w-full" placeholder="地图选点自动回填" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="纬度" prop="latitude">
            <el-input-number v-model="createForm.latitude" :precision="6" class="!w-full" placeholder="地图选点自动回填" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="登录密码" prop="loginPassword">
            <el-input v-model="createForm.loginPassword" type="password" show-password placeholder="选填，不填则自动生成" maxlength="100" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="12" class="apply-upload-row">
        <el-col :xs="24" :sm="8">
          <el-form-item label="营业执照" prop="businessLicense" label-width="110px">
            <UploadImg v-model="createForm.businessLicense" width="120px" height="120px" :limit="1" />
          </el-form-item>
        </el-col>
        <el-col :xs="24" :sm="8">
          <el-form-item label="身份证正面" prop="idCardFront" label-width="110px">
            <UploadImg v-model="createForm.idCardFront" width="120px" height="120px" :limit="1" />
          </el-form-item>
        </el-col>
        <el-col :xs="24" :sm="8">
          <el-form-item label="身份证背面" prop="idCardBack" label-width="110px">
            <UploadImg v-model="createForm.idCardBack" width="120px" height="120px" :limit="1" />
          </el-form-item>
        </el-col>
        <el-col :xs="24" :sm="8">
          <el-form-item label="店铺 Logo" prop="shopLogo" label-width="110px">
            <UploadImg v-model="createForm.shopLogo" width="120px" height="120px" :limit="1" />
          </el-form-item>
        </el-col>
        <el-col :xs="24" :sm="8">
          <el-form-item label="特殊资质" prop="qualificationUrl" label-width="110px">
            <UploadImg v-model="createForm.qualificationUrl" width="120px" height="120px" :limit="1" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-divider content-position="left">结算信息（选填）</el-divider>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="开户名称" prop="bankName">
            <el-input v-model="createForm.bankName" placeholder="选填" maxlength="100" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="开户银行" prop="bankType">
            <el-input v-model="createForm.bankType" placeholder="选填，如：中国工商银行" maxlength="50" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="银行账号" prop="bankAccount">
            <el-input v-model="createForm.bankAccount" placeholder="选填" maxlength="50" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="开户支行" prop="bankBranch">
            <el-input v-model="createForm.bankBranch" placeholder="选填" maxlength="100" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="营业时间" prop="businessHours">
            <el-time-picker
              v-model="businessHoursRange"
              is-range
              range-separator="-"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              format="HH:mm"
              value-format="HH:mm"
              clearable
              class="!w-full"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="人均消费(元)" prop="avgPrice">
            <el-input-number v-model="createForm.avgPrice" :min="0" :precision="2" controls-position="right" class="!w-full" />
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <el-form-item label="店铺简介" prop="shopDesc">
            <el-input v-model="createForm.shopDesc" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="选填" />
          </el-form-item>
        </el-col>
      </el-row>
      <div class="form-tip">登录账号/密码不填时，审核通过后由系统自动生成，请及时联系商家查收。</div>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleCreate">提交入驻申请</el-button>
    </template>
  </el-dialog>
  <AmapDialog ref="amapRef" @confirm="handleAmapConfirm" />
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, nextTick, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import type { FormInstance, FormRules } from 'element-plus';
import { createApply, getApplyPage } from '@/api/merchant/apply';
import { getPlanPage, type MerchantPlanVO } from '@/api/merchant/plan';
import { dateFormatter } from '@/utils/formatTime';
import { ContentWrap } from '@/components/ContentWrap';
import UploadImg from '@/components/UploadFile/src/UploadImg.vue';
import { useMallStandaloneNav } from '@/hooks/web/useMallStandaloneNav';
import { useMallSelfPlatformOnlyGuard } from '@/hooks/web/useMallSelfPlatformOnlyGuard';
import { DICT_TYPE, getDictLabel, getIntDictOptions } from '@/utils/dict';
import { AmapDialog } from '@/components/Amap';

defineOptions({ name: 'MerchantApply' });

const route = useRoute();
const router = useRouter();
const { pushMall } = useMallStandaloneNav();
const { guardPlatformOnlyPage } = useMallSelfPlatformOnlyGuard();
const loading = ref(false);
const list = ref([]);
const total = ref(0);
const dialogVisible = ref(false);
const submitLoading = ref(false);
const createFormRef = ref<FormInstance>();
/** 营业时间区间选择器绑定值：[开始, 结束]，提交时拼成 "HH:mm-HH:mm" */
const businessHoursRange = ref<[string, string] | null>(null);
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  companyName: '',
  auditStatus: undefined as number | undefined,
  /** 景区独立窗默认只看景区入驻（merchant_type=1），避免与餐饮申请串表 */
  merchantType: undefined as number | undefined,
});

/** 是否在景区独立窗 / 景区菜单树下 */
const isScenicContext = computed(() => {
  const p = route.path.toLowerCase();
  return p.includes('/scenicarea') || p.includes('/scenic/');
});

/** 商家类型：取自数据字典 merchant_type（系统管理 → 字典管理 可维护） */
const merchantTypeOptions = computed(() => getIntDictOptions(DICT_TYPE.MERCHANT_TYPE));

function merchantTypeLabel(type: number | undefined) {
  return getDictLabel(DICT_TYPE.MERCHANT_TYPE, type) || '其他';
}

const createForm = ref({
  shopName: '',
  merchantType: 1,
  planId: undefined as number | undefined,
  legalPerson: '',
  contactPhone: '',
  contactEmail: '',
  creditCode: '',
  idCardNo: '',
  loginUsername: '',
  loginPassword: '',
  businessLicense: '',
  idCardFront: '',
  idCardBack: '',
  shopLogo: '',
  qualificationUrl: '',
  address: '',
  longitude: undefined as number | undefined,
  latitude: undefined as number | undefined,
  shopDesc: '',
  bankName: '',
  bankType: '',
  bankAccount: '',
  bankBranch: '',
  businessHours: '',
  avgPrice: undefined as number | undefined,
});

const planOptions = ref<MerchantPlanVO[]>([]);

async function loadPlanOptions() {
  try {
    const data = await getPlanPage({ pageNo: 1, pageSize: 100, status: 0 });
    planOptions.value = (data?.list || []).filter((p: MerchantPlanVO) => p.status === 0);
  } catch {
    planOptions.value = [];
  }
}

const createRules: FormRules = {
  shopName: [{ required: true, message: '请输入店铺名称', trigger: 'blur' }],
  legalPerson: [{ required: true, message: '请输入法人姓名', trigger: 'blur' }],
  contactPhone: [{ required: true, message: '请输入联系电话', trigger: 'blur' }],
  businessLicense: [{ required: true, message: '请上传营业执照', trigger: 'change' }],
  idCardFront: [{ required: true, message: '请上传身份证正面', trigger: 'change' }],
  idCardBack: [{ required: true, message: '请上传身份证背面', trigger: 'change' }],
  loginUsername: [
    {
      validator: (_rule, value, callback) => {
        if (value && !/^[a-zA-Z0-9_]{4,50}$/.test(value)) {
          callback(new Error('账号需为 4-50 位字母、数字或下划线'));
        } else {
          callback();
        }
      },
      trigger: 'blur',
    },
  ],
  loginPassword: [
    {
      validator: (_rule, value, callback) => {
        if (value && value.length < 6) {
          callback(new Error('密码至少 6 位'));
        } else {
          callback();
        }
      },
      trigger: 'blur',
    },
  ],
};

function statusLabel(status: number) {
  const map: Record<number, string> = { 0: '待审核', 1: '已通过', 2: '已拒绝', 3: '已撤销' };
  return map[status] ?? '未知';
}

function statusTagType(status: number) {
  const map: Record<number, string> = { 0: 'warning', 1: 'success', 2: 'danger', 3: 'info' };
  return map[status] ?? 'info';
}

function payStatusLabel(status?: number) {
  const map: Record<number, string> = { 0: '无需付', 1: '待支付', 2: '已支付' };
  return map[status ?? 0] ?? '-';
}

function payStatusTag(status?: number) {
  const map: Record<number, string> = { 0: 'info', 1: 'warning', 2: 'success' };
  return map[status ?? 0] ?? 'info';
}

async function getList() {
  loading.value = true;
  try {
    const data = await getApplyPage(queryParams);
    list.value = data?.list ?? [];
    total.value = data?.total ?? 0;
  } finally {
    loading.value = false;
  }
}

function resetQuery() {
  queryParams.companyName = '';
  queryParams.auditStatus = undefined;
  queryParams.pageNo = 1;
  if (isScenicContext.value) {
    queryParams.merchantType = 1;
  }
  getList();
}

const amapRef = ref<InstanceType<typeof AmapDialog>>();
const openAmapPicker = () => {
  amapRef.value?.open(createForm.value.longitude, createForm.value.latitude);
};
const handleAmapConfirm = (data: {
  longitude: number;
  latitude: number;
  address: string;
  province: string;
  city: string;
  district: string;
}) => {
  createForm.value.longitude = data.longitude;
  createForm.value.latitude = data.latitude;
  if (data.address) {
    createForm.value.address = data.address;
  }
};

function openDetail(id: number) {
  // 景区独立窗下不要跳到 /mall 前缀，否则侧栏/路由错乱
  if (isScenicContext.value) {
    const base = route.path.includes('/ai-Application-center/')
      ? '/ai-Application-center/scenicarea'
      : '/scenicarea';
    router.push({
      path: `${base}/shop-manager/merchant-apply/detail/${id}`,
      query: { ...route.query }
    });
    return;
  }
  pushMall(`/merchant/shop-manager/merchant-apply/detail/${id}`);
}

async function openCreate() {
  await loadPlanOptions();
  const free = planOptions.value.find((p) => !p.price || p.price <= 0);
  createForm.value = {
    shopName: '',
    merchantType: isScenicContext.value ? 1 : 7, // 商城默认商城商家；景区窗仍默认景区
    planId: free?.id,
    legalPerson: '',
    contactPhone: '',
    contactEmail: '',
    creditCode: '',
    idCardNo: '',
    loginUsername: '',
    loginPassword: '',
    businessLicense: '',
    idCardFront: '',
    idCardBack: '',
    shopLogo: '',
    qualificationUrl: '',
    address: '',
    longitude: undefined,
    latitude: undefined,
    shopDesc: '',
    bankName: '',
    bankType: '',
    bankAccount: '',
    bankBranch: '',
    businessHours: '',
    avgPrice: undefined,
  };
  businessHoursRange.value = null;
  dialogVisible.value = true;
  nextTick(() => createFormRef.value?.clearValidate());
}

async function handleCreate() {
  const valid = await createFormRef.value?.validate().catch(() => false);
  if (!valid) {
    ElMessage.warning('请完善必填项（含营业执照与身份证正反面）');
    return;
  }
  submitLoading.value = true;
  try {
    const data: Record<string, any> = { ...createForm.value };
    // 营业时间：区间选择器 -> "HH:mm-HH:mm" 字符串
    data.businessHours = businessHoursRange.value ? businessHoursRange.value.join('-') : '';
    if (data.avgPrice === undefined || data.avgPrice === null) {
      delete data.avgPrice;
    }
    if (data.longitude === undefined || data.longitude === null) {
      delete data.longitude;
    }
    if (data.latitude === undefined || data.latitude === null) {
      delete data.latitude;
    }
    data.qualificationUrls = data.qualificationUrl
      ? JSON.stringify([data.qualificationUrl])
      : undefined;
    delete data.qualificationUrl;
    // 选填空串 / null 不传，避免后端 @Email/@Size(min) 等对 "" 误校验且前端只看到空白 toast
    for (const key of [
      'contactEmail',
      'loginUsername',
      'loginPassword',
      'creditCode',
      'idCardNo',
      'shopLogo',
      'shopDesc',
      'address',
      'bankName',
      'bankType',
      'bankAccount',
      'bankBranch',
      'businessHours',
    ]) {
      const value = data[key];
      if (value == null || (typeof value === 'string' && !value.trim())) {
        delete data[key];
      }
    }
    if (isScenicContext.value && (data.merchantType == null || data.merchantType === '')) {
      data.merchantType = 1;
    }
    await createApply(data);
    ElMessage.success('新增成功，已进入待审核');
    dialogVisible.value = false;
    getList();
  } catch (e: any) {
    const tip = (e?.msg || e?.message || '').trim();
    // 拦截器已提示时可能重复，但可避免「空白红条」无文案
    ElMessage.error(tip && tip !== 'Error' ? tip : '提交入驻申请失败，请检查填写内容后重试');
  } finally {
    submitLoading.value = false;
  }
}

onMounted(() => {
  if (guardPlatformOnlyPage()) {
    return;
  }
  if (isScenicContext.value) {
    queryParams.merchantType = 1;
  }
  getList();
});
</script>

<style scoped lang="scss">
.apply-upload-row {
  :deep(.el-form-item) {
    margin-bottom: 18px;
  }

  :deep(.el-form-item__content) {
    min-width: 0;
    overflow: hidden;
  }
}
</style>
